export const ACTIONS_COLUMN_KEY = "actions";

const isActionsColumnKey = (key) =>
  key === "actions" || key === "action" || key === "Actions";

/** Split Actions (always last) from customizable data columns. */
export const splitTableColumns = (columns = []) => {
  const dataColumns = [];
  let actionsColumn = null;

  columns.forEach((column) => {
    if (!column) return;
    if (isActionsColumnKey(column.key)) {
      actionsColumn = column;
      return;
    }
    dataColumns.push(column);
  });

  return { dataColumns, actionsColumn };
};

export const getDefaultColumnKeys = (columns = [], preferredDefaultKeys) => {
  const { dataColumns } = splitTableColumns(columns);
  const available = dataColumns.map((column) => column.key).filter(Boolean);

  if (Array.isArray(preferredDefaultKeys) && preferredDefaultKeys.length) {
    const availableSet = new Set(available);
    const picked = preferredDefaultKeys.filter((key) => availableSet.has(key));
    if (picked.length) return picked;
  }

  // Fallback: first 3 data columns (Actions is appended separately → 4 total)
  return available.slice(0, 3);
};

/**
 * Apply saved ordered keys to full column defs.
 * Unknown/missing keys are skipped; Actions is always appended when present.
 */
export const applyColumnPreference = (
  columns = [],
  preferredKeys,
  preferredDefaultKeys,
) => {
  const { dataColumns, actionsColumn } = splitTableColumns(columns);
  const byKey = new Map(dataColumns.map((column) => [column.key, column]));
  const fallbackKeys = getDefaultColumnKeys(columns, preferredDefaultKeys);

  const isExplicitPreference = Array.isArray(preferredKeys);
  const keys = isExplicitPreference ? preferredKeys : fallbackKeys;

  const ordered = [];
  const used = new Set();

  keys.forEach((key) => {
    if (!key || isActionsColumnKey(key) || used.has(key)) return;
    const column = byKey.get(key);
    if (!column) return;
    ordered.push(column);
    used.add(key);
  });

  // If preference had keys but none matched this status (e.g. status changed), fall back to defaults
  const hadPreferenceForOtherStatus =
    isExplicitPreference && preferredKeys.length > 0 && !ordered.length;

  if (!ordered.length && (!isExplicitPreference || hadPreferenceForOtherStatus)) {
    fallbackKeys.forEach((key) => {
      const column = byKey.get(key);
      if (column && !used.has(key)) {
        ordered.push(column);
        used.add(key);
      }
    });
  }

  if (actionsColumn) {
    ordered.push(actionsColumn);
  }

  return ordered;
};

/** Merge a visible ordered subset back into the full preference list. */
export const mergeVisibleOrderIntoPreference = (
  preferenceKeys = [],
  visibleOrderedKeys = [],
  availableKeys = [],
) => {
  const available = new Set(availableKeys);
  const result = [];
  const used = new Set();

  visibleOrderedKeys.forEach((key) => {
    if (!key || isActionsColumnKey(key) || used.has(key)) return;
    result.push(key);
    used.add(key);
  });

  preferenceKeys.forEach((key) => {
    if (!key || isActionsColumnKey(key) || used.has(key)) return;
    // Keep keys that are not currently available (other status columns)
    if (!available.has(key)) {
      result.push(key);
      used.add(key);
    }
  });

  return result;
};

/**
 * Attach HTML5 drag-and-drop handlers to data column headers (not Actions).
 */
export const withHeaderColumnDrag = (columns = [], onReorderByKey) => {
  const { dataColumns, actionsColumn } = splitTableColumns(columns);
  const visibleKeys = dataColumns.map((column) => column.key);

  const enhanced = dataColumns.map((column) => {
    const previousOnHeaderCell = column.onHeaderCell;

    return {
      ...column,
      onHeaderCell: (col) => {
        const base =
          typeof previousOnHeaderCell === "function"
            ? previousOnHeaderCell(col) || {}
            : {};

        return {
          ...base,
          className: [base.className, "draggable-table-header-cell"]
            .filter(Boolean)
            .join(" "),
          draggable: true,
          onDragStart: (event) => {
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", column.key);
            event.currentTarget.classList.add("is-dragging-header");
            base.onDragStart?.(event);
          },
          onDragOver: (event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            base.onDragOver?.(event);
          },
          onDrop: (event) => {
            event.preventDefault();
            const fromKey = event.dataTransfer.getData("text/plain");
            const toKey = column.key;
            if (fromKey && toKey && fromKey !== toKey) {
              onReorderByKey?.(fromKey, toKey, visibleKeys);
            }
            base.onDrop?.(event);
          },
          onDragEnd: (event) => {
            event.currentTarget.classList.remove("is-dragging-header");
            base.onDragEnd?.(event);
          },
          title: base.title || "Drag to reorder column",
        };
      },
    };
  });

  if (actionsColumn) {
    enhanced.push(actionsColumn);
  }

  return enhanced;
};

/** Dropdown list order: visible (table order) first, then unchecked options. */
export const orderDropdownOptions = (
  availableOptions = [],
  visibleOrderedKeys = [],
) => {
  const byKey = new Map(availableOptions.map((option) => [option.key, option]));
  const ordered = [];
  const used = new Set();

  visibleOrderedKeys.forEach((key) => {
    const option = byKey.get(key);
    if (!option || used.has(key)) return;
    ordered.push(option);
    used.add(key);
  });

  availableOptions.forEach((option) => {
    if (used.has(option.key)) return;
    ordered.push(option);
    used.add(option.key);
  });

  return ordered;
};
